import * as React from "react";
import { PersonAddRegular, ArrowExitRegular, BookRegular, PersonSwapRegular, ChevronDownRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { useHeaderStyles } from "../styles/header.styles";
import { msalInstance, tokenBridgeUserReady, type TokenBridgeUser } from "../auth/AuthProvider";
import { loginRequest } from "../auth/msalConfig";
import { useTenant } from "../auth/TenantContext";
import { ProjectSelector } from "./ProjectSelector";

const useLocalTokenBridge = import.meta.env.VITE_USE_LOCAL_TOKEN_BRIDGE === "true";

function getInitials(name?: string | null): string {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface HeaderProps {
    projectName?: string;
    resourceId?: string;
    projectId?: string;
    onBack?: () => void;
    appUrl?: string;
    /** When true, show "App Testing Agent" with PREVIEW badge and disabled project selector */
    showAppTitle?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ projectName, resourceId, projectId, onBack, appUrl, showAppTitle }) => {
    const s = useHeaderStyles();
    const { selectTenant, selectedTenantId, selectedTenantName, availableTenants } = useTenant();

    // For the token bridge flow, wait for user info from the decoded JWT
    const [bridgeUser, setBridgeUser] = React.useState<TokenBridgeUser | null>(null);
    React.useEffect(() => {
        if (useLocalTokenBridge) {
            void tokenBridgeUserReady.then(setBridgeUser);
        }
    }, []);

    const account = useLocalTokenBridge ? null : msalInstance.getActiveAccount();
    const name = bridgeUser?.name ?? account?.name;
    const username = bridgeUser?.username ?? account?.username;
    const initials = getInitials(name);
    const displayName = name ?? username ?? "";
    const tenantLabel = selectedTenantName ?? bridgeUser?.tenantId ?? account?.username?.split("@")[1] ?? "";

    const [userMenuOpen, setUserMenuOpen] = React.useState(false);
    const [dirMenuOpen, setDirMenuOpen] = React.useState(false);
    const userMenuRef = React.useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setUserMenuOpen(false);
                setDirMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSignOut = () => {
        setUserMenuOpen(false);
        void msalInstance.logoutRedirect({ account });
    };

    const handleSignInOther = () => {
        setUserMenuOpen(false);
        void msalInstance.loginRedirect({ ...loginRequest, prompt: "select_account" });
    };

    const handleSwitchDirectory = (tenantId: string) => {
        setUserMenuOpen(false);
        setDirMenuOpen(false);
        selectTenant(tenantId);
        // Check if we have a saved project URL for the target tenant
        const savedUrl = localStorage.getItem(`dashboard.lastProjectUrl.${tenantId}`);
        if (savedUrl) {
            window.location.href = savedUrl;
        } else {
            // No known project for this tenant — show the no-subscription page
            window.location.href = "/no-subscription";
        }
    };

    return (
        <div className={s.header}>
            <img src="/icons/app-icon.svg" alt="" className={s.icon} />
            <div className={s.breadcrumb}>
                {showAppTitle ? (
                    <>
                        <span className={s.appName}>App Testing Agent</span>
                        <span className={s.previewTag}>PREVIEW</span>
                    </>
                ) : (
                    <>
                        {onBack && (
                            <>
                                <button className={s.backBtn} onClick={onBack}>
                                    App Testing Agent
                                </button>
                                <span className={s.previewTag}>PREVIEW</span>
                                <span className={s.breadcrumbSep}>/</span>
                            </>
                        )}
                        <span className={s.breadcrumbText}>
                            {resourceId && projectId ? (
                                <ProjectSelector
                                    resourceId={resourceId}
                                    currentProjectId={projectId}
                                    currentProjectName={projectName ?? projectId}
                                />
                            ) : (
                                projectName ?? <span style={{ opacity: 0.4 }}>…</span>
                            )}
                        </span>
                    </>
                )}
                {appUrl && (
                    <a className={s.openAppBtn} href={appUrl} target="_blank" rel="noreferrer">
                        Open app
                    </a>
                )}
            </div>
            <div className={s.rightGroup}>
                <a
                    href="https://turbo-telegram-qjwo9wj.pages.github.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.docsBtn}
                    title="Docs"
                >
                    <BookRegular style={{ fontSize: 20 }} />
                </a>
                {/* Avatar + user menu */}
                <div className={s.userMenuWrapper} ref={userMenuRef}>
                    <button
                        className={s.avatarBtn}
                        onClick={() => setUserMenuOpen(o => !o)}
                        title={displayName}
                        aria-label="User menu"
                    >
                        {initials}
                    </button>
                    {userMenuOpen && (
                        <div className={s.userMenu}>
                            <div className={s.userMenuTopRow}>
                            <span className={s.userMenuOrg}>{tenantLabel}</span>
                                <button className={s.userMenuSignOutBtn} onClick={handleSignOut}>
                                    <ArrowExitRegular style={{ fontSize: 20 }} />
                                    Sign out
                                </button>
                            </div>
                            <div className={s.userMenuProfile}>
                                <div className={s.userMenuAvatarLarge}>{initials}</div>
                                <div className={s.userMenuProfileInfo}>
                                    <span className={s.userMenuName}>{displayName || "Signed in"}</span>
                                    {username && (
                                        <span className={s.userMenuEmail}>{username}</span>
                                    )}
                                </div>
                            </div>
                            <div className={s.userMenuDivider} />
                            <div style={{ position: "relative" }}>
                                <button
                                    className={`${s.userMenuItem} ${dirMenuOpen ? s.userMenuItemActive : ""}`}
                                    onClick={() => setDirMenuOpen(o => !o)}
                                >
                                    <PersonSwapRegular style={{ fontSize: 20, flexShrink: 0 }} />
                                    <span style={{ flex: 1 }}>Switch Directory</span>
                                    <ChevronDownRegular style={{ fontSize: 16, flexShrink: 0, transform: dirMenuOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                                </button>
                                {dirMenuOpen && availableTenants.length > 0 && (
                                    <div className={s.dirFlyout}>
                                        {availableTenants.map(t => (
                                            <button
                                                key={t.tenantId}
                                                className={s.dirFlyoutItem}
                                                onClick={() => handleSwitchDirectory(t.tenantId)}
                                            >
                                                <span className={s.dirFlyoutCheckmark}>
                                                    {t.tenantId === selectedTenantId && <CheckmarkRegular style={{ fontSize: 16 }} />}
                                                </span>
                                                <span className={s.dirFlyoutItemInfo}>
                                                    <span className={s.dirFlyoutItemName}>{t.displayName || "Directory"}</span>
                                                    <span className={s.dirFlyoutItemDomain}>{t.tenantId}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={s.userMenuDivider} />
                            <button className={s.userMenuItem} onClick={handleSignInOther}>
                                <PersonAddRegular style={{ fontSize: 20, flexShrink: 0 }} />
                                Sign in with a different account
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
